import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  input,
  signal,
  HostListener,
  ElementRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

export interface DropdownOption {
  value: any;
  name: string;
  [key: string]: any;
}

@Component({
  selector: 'lib-custom-dropdown',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  template: `
    <div class="flex flex-col gap-2 relative">
      @if (label()) {
        <label class="text-[14px] font-medium text-[#AAB3C1]">{{
          label()
        }}</label>
      }
      <div
        class="w-full border border-[#E2E8F6] rounded-full bg-white text-sm transition-all duration-200 cursor-pointer flex items-center justify-between select-none hover:border-slate-300"
        [class.py-2]="size() === 'small'"
        [class.px-3]="size() === 'small'"
        [class.py-3]="size() === 'medium' || size() === 'large'"
        [class.px-4]="size() === 'medium' || size() === 'large'"
        [class.border-[#6887ED]]="isOpen()"
        [class.shadow-[0_0_0_3px_rgba(104,135,237,0.1)]="isOpen()"
        (click)="toggleDropdown()"
      >
        <span
          [style.color]="
            value() !== null && value() !== undefined && value() !== ''
              ? '#161D27'
              : '#AAB3C1'
          "
          style="font-size: 16px"
          >{{ getSelectedLabel() }}</span
        >
        <i
          class="pi pi-chevron-down text-slate-400 text-xs transition-transform duration-200"
          [class.rotate-180]="isOpen()"
        ></i>
      </div>
      @if (isOpen()) {
        <div
          class="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.1)] z-[100] overflow-hidden max-h-60 overflow-y-auto"
          [style.top]="label() ? 'calc(100% + 4px)' : 'calc(100% + 4px)'"
        >
          @for (option of options(); track option.value) {
            <div
              class="py-3 px-[18px] text-[14px] text-slate-800 cursor-pointer transition-all duration-150 hover:bg-slate-100"
              [class.bg-blue-50]="value() === option.value"
              [class.text-[#6887ED]]="value() === option.value"
              [class.font-medium]="value() === option.value"
              (click)="selectOption(option)"
            >
              {{
                isTranslated()
                  ? (option[optionLabel()] | transloco)
                  : option[optionLabel()]
              }}
            </div>
          }
        </div>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true,
    },
  ],
})
export class CustomDropdownComponent implements ControlValueAccessor {
  private elementRef = inject(ElementRef);
  private translocoService = inject(TranslocoService);

  // Inputs
  label = input<string>('');
  options = input.required<DropdownOption[]>();
  optionLabel = input<string>('name');
  optionValue = input<string>('value');
  placeholder = input<string>('Select an option');
  isTranslated = input<boolean>(true);
  size = input<'small' | 'medium' | 'large'>('medium');

  // State
  isOpen = signal(false);
  value = signal<any>(null);
  isDisabled = false;

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  toggleDropdown() {
    if (!this.isDisabled) {
      this.isOpen.update((open) => !open);
    }
  }

  selectOption(option: DropdownOption) {
    this.value.set(option[this.optionValue()]);
    this.onChange(this.value());
    this.onTouched();
    this.isOpen.set(false);
  }

  getSelectedLabel(): string {
    const val = this.value();
    if (val === null || val === undefined || val === '') {
      return this.isTranslated()
        ? this.translocoService.translate(this.placeholder())
        : this.placeholder();
    }
    const selected = this.options().find(
      (opt) => opt[this.optionValue()] === val,
    );
    if (!selected) {
      return this.isTranslated()
        ? this.translocoService.translate(this.placeholder())
        : this.placeholder();
    }
    const labelText = selected[this.optionLabel()];
    return this.isTranslated()
      ? this.translocoService.translate(labelText)
      : labelText;
  }

  getTextColor(): string {
    const val = this.value();
    return val !== null && val !== undefined && val !== ''
      ? '#161D27'
      : '#AAB3C1';
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
